/**
 * 多项目检测作业全局 Store（方案 §需求4）。
 *
 * 职责：
 * 1. 维护活跃的多项目作业列表，独立于创建弹窗的生命周期轮询进度；
 * 2. 弹窗提交后注册 job 即立即关闭，进度由右上角悬浮组件展示；
 * 3. completed → message.success + 移除；failed → message.error + 延迟移除；
 *    cancelled → message.warning + 移除。
 *
 * 设计要点：
 * - Provider 挂在 App 根（AntdApp 内、RouterProvider 外），保证弹窗关闭后轮询继续；
 * - 用 ref 存储 poller 取消标记，组件卸载时统一停止所有轮询；
 * - 轮询逻辑移植自 useTaskCreationFlow.pollMultiProjectJob，超时动态计算。
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { App } from 'antd';
import {
  multiProjectTaskApi,
  type MultiProjectJobStatus,
} from '@/modules/detection-task/services/multiProjectTaskApi';
import { getApiErrorMessage } from '@/modules/detection-task/utils/apiError';

/** 单个活跃作业的元信息（注册时由提交逻辑传入） */
export interface MultiProjectJobMeta {
  /** 提交时选中的项目 ID（用于悬浮组件展示） */
  selectedProjectIds: string[];
  /** 提交时间戳 */
  createdAt: number;
}

/** 活跃作业条目 */
export interface ActiveJob {
  jobId: string;
  status: MultiProjectJobStatus | null;
  meta: MultiProjectJobMeta;
}

interface MultiProjectJobStoreValue {
  /** 活跃作业列表 */
  jobs: ActiveJob[];
  /** 注册作业并启动轮询（提交成功后调用） */
  registerJob: (jobId: string, meta: MultiProjectJobMeta) => void;
  /** 取消作业（best-effort，轮询循环检测到 cancelled 后清理） */
  cancelJob: (jobId: string) => Promise<void>;
}

const MultiProjectJobStoreContext = createContext<MultiProjectJobStoreValue | null>(null);

/** 轮询间隔（毫秒），与原 pollMultiProjectJob 的 1s 一致 */
const POLL_INTERVAL_MS = 1000;

export const MultiProjectJobStoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { message } = App.useApp();
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  // 每个 jobId 对应一个取消标记；组件卸载或作业终态时标记 true 停止轮询
  const pollersRef = useRef(new Map<string, { cancelled: boolean }>());

  const removeJob = useCallback((jobId: string) => {
    const poller = pollersRef.current.get(jobId);
    if (poller) poller.cancelled = true;
    pollersRef.current.delete(jobId);
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  const updateJobStatus = useCallback((jobId: string, status: MultiProjectJobStatus) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === jobId ? { ...j, status } : j)),
    );
  }, []);

  const pollJob = useCallback(
    async (jobId: string, meta: MultiProjectJobMeta) => {
      const poller = { cancelled: false };
      pollersRef.current.set(jobId, poller);

      // 第一次查询以确定 projects_total 用于动态超时
      let firstStatus: MultiProjectJobStatus | null = null;
      try {
        const resp = await multiProjectTaskApi.getJob(jobId);
        firstStatus = resp.data;
        updateJobStatus(jobId, firstStatus);
      } catch {
        // 网络错误继续轮询
      }

      const projectsTotal = firstStatus?.projects_total ?? Math.max(meta.selectedProjectIds.length, 1);
      const maxAttempts = Math.max(120, 120 + projectsTotal * 30);

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (poller.cancelled) return;
        try {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          if (poller.cancelled) return;

          const { data: status } = await multiProjectTaskApi.getJob(jobId);
          updateJobStatus(jobId, status);

          if (status.status === 'completed') {
            message.success(`多项目作业完成，共创建 ${status.tasks_created} 个任务`, 4);
            if (status.skipped_projects.length > 0) {
              const reasons = status.skipped_projects
                .map((p) => `${p.project_id}: ${p.reason}`)
                .join('；');
              message.warning(
                `跳过 ${status.skipped_projects.length} 个项目（${reasons}）`,
                8,
              );
            }
            removeJob(jobId);
            return;
          }
          if (status.status === 'failed') {
            message.error(status.error_message || '作业失败', 6);
            // 延迟 8 秒移除，让悬浮组件有时间展示错误信息
            setTimeout(() => removeJob(jobId), 8000);
            return;
          }
          if (status.status === 'cancelled') {
            message.warning('多项目作业已取消', 4);
            removeJob(jobId);
            return;
          }
          // running / pending：进度由悬浮组件实时展示
        } catch {
          // 单次轮询网络异常忽略，继续轮询
        }
      }
      // 超时：作业仍在后台处理
      message.warning('作业仍在后台处理，请稍后查看任务列表', 6);
      removeJob(jobId);
    },
    [message, removeJob, updateJobStatus],
  );

  const registerJob = useCallback(
    (jobId: string, meta: MultiProjectJobMeta) => {
      setJobs((prev) => [...prev, { jobId, status: null, meta }]);
      void pollJob(jobId, meta);
    },
    [pollJob],
  );

  const cancelJob = useCallback(
    async (jobId: string) => {
      try {
        await multiProjectTaskApi.cancelJob(jobId);
        message.info('取消请求已提交，轮询循环将自动清理');
        // 轮询循环会检测到 cancelled 状态并清理
      } catch (error: any) {
        message.error(getApiErrorMessage(error, '取消作业失败'));
      }
    },
    [message],
  );

  // 组件卸载时停止所有轮询
  useEffect(() => {
    return () => {
      pollersRef.current.forEach((p) => {
        p.cancelled = true;
      });
      pollersRef.current.clear();
    };
  }, []);

  return (
    <MultiProjectJobStoreContext.Provider value={{ jobs, registerJob, cancelJob }}>
      {children}
    </MultiProjectJobStoreContext.Provider>
  );
};

export function useMultiProjectJobStore(): MultiProjectJobStoreValue {
  const ctx = useContext(MultiProjectJobStoreContext);
  if (!ctx) {
    throw new Error(
      'useMultiProjectJobStore 必须在 MultiProjectJobStoreProvider 内调用',
    );
  }
  return ctx;
}
